import { mergeAttributes, Node } from "@tiptap/core";
import { PluginKey } from "@tiptap/pm/state";
import { ReactNodeViewRenderer } from "@tiptap/react";
import type { SuggestionOptions } from "@tiptap/suggestion";
import Suggestion from "@tiptap/suggestion";

import TagMentionDisplay from "./TagMentionDisplay";
import {
  TagMentionAttrs,
  TagSuggestionItem,
  entityToTagMentionAttrs,
  isTagUser,
  isTagEntity,
  isTagEntityType,
} from "./types";

export interface TagMentionOptions {
  HTMLAttributes: Record<string, unknown>;
  suggestion: Omit<SuggestionOptions<TagSuggestionItem>, "editor">;
}

export interface TagMentionStorage {
  suggestion: SuggestionOptions<TagSuggestionItem> | null;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    tagMention: {
      insertTag: (attrs: TagMentionAttrs) => ReturnType;
    };
  }
}

export const TagMention = Node.create<TagMentionOptions, TagMentionStorage>({
  name: "tagMention",

  priority: 102,

  addStorage() {
    return {
      suggestion: null,
    };
  },

  addOptions() {
    return {
      HTMLAttributes: {},
      suggestion: {
        char: "@",
        pluginKey: new PluginKey("tagMention"),
        command: ({ editor, range, props }) => {
          const { nodeAfter } = editor.view.state.selection.$to;
          const overrideSpace = nodeAfter?.text?.startsWith(" ");

          if (overrideSpace) {
            range.to += 1;
          }

          // Convert TagSuggestionItem to TagMentionAttrs
          let attrs: TagMentionAttrs;
          if (isTagUser(props)) {
            attrs = {
              tagType: "user",
              id: props.id,
              name: props.name,
              email: props.email,
            };
          } else if (isTagEntityType(props)) {
            attrs = {
              tagType: "entityType",
              name: props.displayName,
              entityType: props.entityType,
            };
          } else if (isTagEntity(props)) {
            attrs = entityToTagMentionAttrs(props.entity);
          } else {
            // Fallback (should not happen)
            attrs = {
              tagType: "entity",
              id: 0,
              name: "Unknown",
            };
          }

          editor
            .chain()
            .focus()
            .insertContentAt(range, [
              {
                type: "tagMention",
                attrs,
              },
              {
                type: "text",
                text: " ",
              },
            ])
            .run();

          editor.view.dom.ownerDocument.defaultView?.getSelection()?.collapseToEnd();
        },
        allow: ({ state, range }) => {
          const $from = state.doc.resolve(range.from);
          const type = state.schema.nodes.tagMention;
          if (!type) {
            return true;
          }
          return !!$from.parent.type.contentMatch.matchType(type);
        },
      },
    };
  },

  group: "inline",

  inline: true,

  selectable: false,

  atom: true,

  addAttributes() {
    return {
      tagType: {
        default: "user",
        parseHTML: element => element.getAttribute("data-tag-type"),
        renderHTML: attributes => ({
          "data-tag-type": attributes.tagType,
        }),
      },
      id: {
        default: null,
        parseHTML: element => element.getAttribute("data-id"),
        renderHTML: attributes => ({
          "data-id": attributes.id,
        }),
      },
      name: {
        default: null,
        parseHTML: element => element.getAttribute("data-name"),
        renderHTML: attributes => ({
          "data-name": attributes.name,
        }),
      },
      email: {
        default: null,
        parseHTML: element => element.getAttribute("data-email"),
        renderHTML: attributes => ({
          "data-email": attributes.email,
        }),
      },
      entityType: {
        default: null,
        parseHTML: element => element.getAttribute("data-entity-type"),
        renderHTML: attributes => ({
          "data-entity-type": attributes.entityType,
        }),
      },
      link: {
        default: null,
        parseHTML: element => element.getAttribute("data-link"),
        renderHTML: attributes => ({
          "data-link": attributes.link,
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: `span[data-type="${this.name}"]`,
      },
    ];
  },

  renderText({ node }) {
    const attrs = node.attrs as TagMentionAttrs;

    // Format: @@@tag:tagType:id:name:email:entityType:link@@@
    // Empty values are represented as empty strings between colons
    const parts = [
      "tag",
      attrs.tagType || "",
      attrs.id?.toString() || "",
      attrs.name || "",
      attrs.email || "",
      attrs.entityType || "",
      attrs.link || "",
    ];

    return `@@@${parts.join(":")}@@@`;
  },

  renderHTML({ node, HTMLAttributes }) {
    const attrs = node.attrs as TagMentionAttrs;

    if (attrs.tagType === "user") {
      return [
        "span",
        mergeAttributes({ "data-type": this.name }, this.options.HTMLAttributes, HTMLAttributes, {
          class: "tag-mention tag-mention-user",
          style:
            "color: rgb(37, 99, 235); font-weight: 500; background-color: rgb(239, 246, 255); padding: 2px 4px; border-radius: 4px;",
        }),
        `@${attrs.name}`,
      ];
    }

    if (attrs.tagType === "entityType") {
      const displayName = attrs.name.charAt(0).toUpperCase() + attrs.name.slice(1);
      return [
        "span",
        mergeAttributes({ "data-type": this.name }, this.options.HTMLAttributes, HTMLAttributes, {
          class: "tag-mention tag-mention-entity-type",
          style:
            "display: inline-flex; align-items: center; gap: 4px; border: 1px solid rgb(209, 213, 219); background-color: white; padding: 4px 8px; border-radius: 6px; font-size: 12px; font-weight: 500;",
        }),
        displayName,
      ];
    }

    // Entity rendering - fallback for non-React contexts (e.g., copy/paste, export)
    return [
      "span",
      mergeAttributes({ "data-type": this.name }, this.options.HTMLAttributes, HTMLAttributes, {
        class: "tag-mention tag-mention-entity",
        style:
          "display: inline-flex; align-items: center; gap: 4px; border: 1px solid rgb(209, 213, 219); background-color: white; padding: 4px 8px; border-radius: 6px; font-size: 12px;",
      }),
      attrs.name,
    ];
  },

  addCommands() {
    return {
      insertTag:
        attrs =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs,
          }),
    };
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(TagMentionDisplay, {
      contentDOMElementTag: "span",
    });
  },

  onBeforeCreate() {
    this.storage.suggestion = {
      editor: this.editor,
      ...this.options.suggestion,
    };
  },
});

export default TagMention;
